<script setup>
import moment from 'moment';
import random from 'lodash/random';
import RolesPicker from '/app/components/game/RolesPicker.vue';
import { useInterval } from '/app/composables/interval';
import { useAuth } from '/app/composables/auth';
import { useResponsive } from '/app/composables/responsive';
import { ProgressLineChart } from '@bignerve/ui-charts';
import { rankColors, roleColors, series as initialSeries } from './neurons-earned-data';
import { scaleLinear } from 'd3';
import { ref, computed, watch } from 'vue';
import { useBus } from '/app/composables/bus';

const { emit: $bus } = useBus();

const yes = (p = 0.5) => Math.random() < p;
const mockPoints = (serie) => {
    const previewSerie = serie.history[serie.history.length - 1] || { points: 0 };
    const growthRate = serie.growthRate || 1000;

    return yes()
        ? previewSerie.points + Math.floor(Math.random() * growthRate)
        : previewSerie.points;
};

defineProps({
    control: {
        type: Boolean,
        default: false,
    },
});

const { auth } = useAuth();
const responsive = useResponsive();

const role = ref(null);
const step = ref(0);
const series = ref(initialSeries);

// Forward-declared so the interval handler closure can call it.
let advanceStep;

const {
    intervalActive,
    intervalStartUpdater,
    intervalStopUpdater,
} = useInterval({
    handler: () => advanceStep(),
    immediate: false,
    ms: 100,
});

const chartOptions = computed(() => {
    const r = role.value ? role.value.slug : 'overall';

    return {
        colors: rankColors,
        marks: {
            lineColor: roleColors[r],
        },
        xLabels: {
            format: (d) => moment(d).format('MMMM'),
        },
        height: responsive.sm && 500,
    };
});

const historical = computed(() =>
    series.value.map((d) => ({
        ...d,
        highlighted: d.user.screen_name === auth.user.screen_name,
    })),
);

advanceStep = function (overrideStep) {
    const serieScale = scaleLinear()
        .domain([0, 100])
        .range([0, 10])
        .nice();

    step.value = overrideStep != null ? Number(overrideStep) : step.value + 1;

    series.value = series.value.map((serie, i) => {
        const mockedPoints = mockPoints(serie);
        let level = yes(0.08) ? random(serie.level, step.value) : serie.level;

        if (i === 0 && step.value > 92) {
            level = 100;
        }

        serie.level = level;
        serie.rank = serieScale(step.value);
        serie.score = mockedPoints;
        serie.history.push({
            date: moment().utc().add(step.value, 'days').unix(),
            points: mockedPoints,
            level,
        });

        return serie;
    });

    if (step.value >= 100) {
        intervalStopUpdater();
    }
};

function clear() {
    step.value = 0;
    series.value = series.value.map((serie) => {
        serie.history = [
            {
                date: moment().utc().subtract(1, 'day').unix(),
                points: 0,
                level: 0,
            },
            {
                date: moment().utc().unix(),
                points: 0,
                level: 0,
            },
        ];
        serie.rank = 0;
        serie.level = 0;
        serie.score = 0;
        serie.growthRate = random(500, 1500);

        return serie;
    });
}

function reset() {
    clear();
    intervalStopUpdater();
    intervalStartUpdater();
}

watch(
    () => auth.user,
    (newUser, oldUser) => {
        if (newUser) {
            series.value[0].user = newUser;
        }

        if (!newUser || newUser !== oldUser) {
            reset();
        }
    },
    { immediate: true },
);
</script>

<template>
    <div>
        <RolesPicker v-model="role" @input="reset" />

        <ProgressLineChart
            :series="historical"
            :options="chartOptions"
            class="progress-line-chart w-full h-150"
        />

        <div v-if="control" class="flex items-center ml-7">
            <button aria-label="button" v-if="step === 100" type="button" class="border rounded-md w-10 h-8 flex items-center justify-center text-dust-700 hover:bg-dust-200" @click="reset">
                <svg class="w-4 h-4" aria-hidden="true" focusable="false" data-prefix="fas" data-icon="redo-alt" role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512"><path fill="currentColor" d="M256.455 8c66.269.119 126.437 26.233 170.859 68.685l35.715-35.715C478.149 25.851 504 36.559 504 57.941V192c0 13.255-10.745 24-24 24H345.941c-21.382 0-32.09-25.851-16.971-40.971l41.75-41.75c-30.864-28.899-70.801-44.907-113.23-45.273-92.398-.798-170.283 73.977-169.484 169.442C88.764 348.009 162.184 424 256 424c41.127 0 79.997-14.678 110.629-41.556 4.743-4.161 11.906-3.908 16.368.553l39.662 39.662c4.872 4.872 4.631 12.815-.482 17.433C378.202 479.813 319.926 504 256 504 119.034 504 8.001 392.967 8 256.002 7.999 119.193 119.646 7.755 256.455 8z"></path></svg>
            </button>
            <button aria-label="button" v-else-if="!intervalActive" type="button" class="border rounded-md w-10 h-8 flex items-center justify-center text-dust-700 hover:bg-dust-200" @click="$bus('game-dash:state', 'closed'); intervalStartUpdater()">
                <svg class="w-4 h-4" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" data-prefix="fas" data-icon="play" role="img" viewBox="0 0 448 512"><path fill="currentColor" d="M424.4 214.7L72.4 6.6C43.8-10.3 0 6.1 0 47.9V464c0 37.5 40.7 60.1 72.4 41.3l352-208c31.4-18.5 31.5-64.1 0-82.6z"/></svg>
            </button>
            <button aria-label="button" v-else type="button" class="border rounded-md w-10 h-8 flex items-center justify-center text-dust-700 hover:bg-dust-200" @click="intervalStopUpdater">
                <svg  class="w-4 h-4" aria-hidden="true" focusable="false" data-prefix="fas" data-icon="pause" role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512"><path fill="currentColor" d="M144 479H48c-26.5 0-48-21.5-48-48V79c0-26.5 21.5-48 48-48h96c26.5 0 48 21.5 48 48v352c0 26.5-21.5 48-48 48zm304-48V79c0-26.5-21.5-48-48-48h-96c-26.5 0-48 21.5-48 48v352c0 26.5 21.5 48 48 48h96c26.5 0 48-21.5 48-48z"></path></svg>
            </button>
            <input :value="step" type="range" id="level" name="level" min="0" max="100" class="rounded-lg appearance-none bg-dust-200 h-2 min-w-40 outline-none range-progre mx-3 w-full disabled" @input="e => advanceStep(e.target.value)" >
        </div>
    </div>
</template>

<style lang="scss">
  input[type='range'].range-progre::-webkit-slider-thumb {
    width: 15px;
    height: 15px;
    @apply bg-dust-600;
    @apply border-2;
    @apply border-white;
    @apply z-10;
    @apply cursor-pointer;
    @apply appearance-none;
    @apply rounded-md;
  }
</style>
